import React, { useState } from 'react';
import { Trophy } from 'lucide-react';
import { Achievement } from '../../types/Achievement';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import AchievementForm, { AchievementFormData } from './AchievementForm';
import { achievementTableConfig } from './AchievementTableConfig';

const EnhancedAchievementManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const actions = {
    handleCreate: () => {
      setEditingAchievement(null);
      setShowForm(true);
    },

    handleEdit: (achievement: Achievement) => {
      setEditingAchievement(achievement);
      setShowForm(true);
    },

    handleView: (achievement: Achievement) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingAchievement(achievement);
      setShowForm(true);
    }
  };

  const handleFormSubmit = async (formData: AchievementFormData) => {
    try {
      setIsLoading(true);

      if (editingAchievement) {
        await apiClient.put(`/api/admin/achievements/${editingAchievement.id}`, formData);
      } else {
        await apiClient.post('/api/admin/achievements', formData);
      }

      setShowForm(false);
      setEditingAchievement(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving achievement:', error);
      alert('Failed to save achievement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAchievement(null);
  };

  const handleDelete = async (achievementId: string) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;

    try {
      await apiClient.delete(`/api/admin/achievements/${achievementId}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete achievement:', error);
      alert('Failed to delete achievement');
    }
  };

  if (showForm) {
    return (
      <AchievementForm
        achievement={editingAchievement}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Achievement Management"
          subtitle="Manage recent achievements and awards for display on the homepage"
          icon={<Trophy size={32} />}
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Trophy size={20} />
            Add Achievement
          </button>
        </div>

        {/* Enhanced Table */}
        <AdminTable<Achievement>
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={handleDelete}
          config={achievementTableConfig}
          refreshTrigger={refreshTrigger}
          isLoading={isLoading}
        />

        {/* Form Modal */}
        {showForm && (
          <AchievementForm
            achievement={editingAchievement}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedAchievementManagement;
